import { useEffect, useMemo, useRef, useState } from "react";

import { useLocation } from "react-router-dom";

import { Spinner } from "@alpacahq/ui/old";

import {
  useGetAcatsTransfers,
  useGetAllRelationships,
  useGetAllTransfers,
  useGetOwnerDetails,
  useIsCryptoSupported,
} from "~/v2/api/hooks";
import { useGetAcatsContraBrokers } from "~/v2/api/hooks/useGetAcatsContraBrokers";
import {
  useGetCryptoAssets,
  useGetCryptoTransfers,
} from "~/v2/api/hooks/useGetCrypto";
import { Tabs } from "~/v2/components/common/Tabs";
import { MFAGuard } from "~/v2/components/MFAGuard/MFAGuard";
import { Page } from "~/v2/components/Page";
import { useMFA } from "~/v2/hooks/useMFA";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import type { FAQItem } from "../../../../components/common/FAQ";
import { CryptoTransferHistoryTable } from "./CryptoTransferHistoryTable";
import { PortfolioTransferTable } from "./PortfolioTransferTable";
import { TransferHistoryTable } from "./TransferHistoryTable";

type FundingHistoryTabContext = {
  hasAcatsTransfers: boolean;
  hasCryptoTransfers: boolean;
};

type FundingHistoryTabDefinition = {
  id: string;
  label: string;
  queryValues: string[];
  isAvailable: (ctx: FundingHistoryTabContext) => boolean;
};

const FUNDING_HISTORY_TAB_DEFINITIONS: FundingHistoryTabDefinition[] = [
  {
    id: "currency",
    label: "Transfers",
    queryValues: ["currency", "transfers"],
    isAvailable: () => true,
  },
  {
    id: "portfolio",
    label: "Portfolio Transfer",
    queryValues: ["portfolio", "portfolio-transfers"],
    isAvailable: ({ hasAcatsTransfers }) => hasAcatsTransfers,
  },
  {
    id: "crypto",
    label: "Crypto",
    queryValues: ["crypto", "crypto-transfers"],
    isAvailable: ({ hasCryptoTransfers }) => hasCryptoTransfers,
  },
];

// @todo: add FAQ to this page
const _HISTORY_FAQ: FAQItem[] = [
  {
    question: "What does 'PENDING' mean?",
    answer: (
      <>
        We have received your transfer request. You may still request to cancel
        your transfer although it is not guaranteed.
      </>
    ),
  },
  {
    question: "What does 'COMPLETE' mean?",
    answer: <>Your transfer request is complete, and your funds are settled.</>,
  },
  {
    question: "What does 'CANCELED' mean?",
    answer: <>Your transfer request has been canceled.</>,
  },
  {
    question: "What does 'EXPIRED' mean?",
    answer: (
      <>
        You were not able to provide the additional information requested to
        verify your transfer request. Please contact{" "}
        <a href="mailto:support@alpaca.markets">support@alpaca.markets</a>.
      </>
    ),
  },
  {
    question: "What does 'REJECTED' mean?",
    answer: (
      <>
        Your transfer request was either rejected by your bank or Alpaca. Please
        contact{" "}
        <a href="mailto:support@alpaca.markets">support@alpaca.markets</a>.
      </>
    ),
  },
  {
    question: "What does 'RETURNED' mean?",
    answer: (
      <>
        Your transfer request could not be processed. Please contact{" "}
        <a href="mailto:support@alpaca.markets">support@alpaca.markets</a>.
      </>
    ),
  },
];

const FundingHistoryPage = () => {
  const location = useLocation();
  const { currentAccount } = useAccountContext({ liveOnly: true });

  const DEFAULT_TAB_ID = "currency";

  const requestedTabValue = useMemo(() => {
    const value = new URLSearchParams(location.search).get("tab");
    return value?.toLowerCase().trim() ?? null;
  }, [location.search]);

  const requestedTabId = useMemo(() => {
    if (!requestedTabValue) {
      return null;
    }
    const match = FUNDING_HISTORY_TAB_DEFINITIONS.find(
      (tab) =>
        tab.id === requestedTabValue ||
        tab.queryValues.includes(requestedTabValue),
    );

    return match?.id ?? null;
  }, [requestedTabValue]);

  const [activeTab, setActiveTab] = useState<string>(
    () => requestedTabId ?? DEFAULT_TAB_ID,
  );
  const hasUserSelectedTabRef = useRef(false);
  const previousRequestedTabIdRef = useRef<string | null>(requestedTabId);

  const { details } = useGetOwnerDetails();

  const isCryptoActive =
    currentAccount?.crypto_status === "ACTIVE" &&
    !currentAccount?.admin_configurations?.disable_crypto;

  const { isCryptoSupported } = useIsCryptoSupported();

  const isCryptoEnabled = isCryptoActive && isCryptoSupported;

  const { isMFAEnabled, isMFALoading } = useMFA();
  const isCryptoWalletEnabled =
    isCryptoEnabled && !isMFALoading && isMFAEnabled;

  const relationships = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  const transfers = useGetAllTransfers({
    accountID: currentAccount?.id || "",
    isCurrencyCloudEnabled: relationships.currencyCloud.isEnabled,
    refetchOnMount: "always",
  });

  const acatsTransfers = useGetAcatsTransfers({
    accountId: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });

  // Fetch contra brokers once at the page level
  const contraBrokers = useGetAcatsContraBrokers({
    enabled: !!currentAccount?.id,
  });

  const cryptoTransfers = useGetCryptoTransfers({
    accountId: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id && isCryptoWalletEnabled,
    },
  });

  const cryptoAssets = useGetCryptoAssets({
    accountId: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id && isCryptoWalletEnabled,
    },
  });

  const isRelationshipsLoading = Object.values(relationships).some(
    (value) => value.isLoading,
  );

  const isTransfersLoading = Object.values(transfers).some(
    (value) => value.isLoading,
  );

  const isCryptoLoading =
    isCryptoWalletEnabled &&
    (cryptoTransfers.isLoading || cryptoAssets.isLoading);
  const isCryptoMFAStatusLoading =
    activeTab === "crypto" && isCryptoEnabled && isMFALoading;

  const hasAcatsTransfers = (acatsTransfers.data?.transfers || []).length > 0;

  const hasCryptoTransfers = (cryptoTransfers.data || []).length > 0;

  const tabs = useMemo(() => {
    const ctx: FundingHistoryTabContext = {
      hasAcatsTransfers,
      hasCryptoTransfers,
    };

    return FUNDING_HISTORY_TAB_DEFINITIONS.filter(
      (tab) => tab.isAvailable(ctx) || requestedTabId === tab.id,
    ).map(({ id, label }) => ({ id, label }));
  }, [hasAcatsTransfers, hasCryptoTransfers, requestedTabId]);

  const showTabs = tabs.length > 1;

  const onTabChange = (tabId: string) => {
    hasUserSelectedTabRef.current = true;
    setActiveTab(tabId);
  };

  useEffect(() => {
    if (previousRequestedTabIdRef.current !== requestedTabId) {
      hasUserSelectedTabRef.current = false;
      previousRequestedTabIdRef.current = requestedTabId;
    }

    const isActiveTabVisible = tabs.some((tab) => tab.id === activeTab);
    if (!isActiveTabVisible) {
      setActiveTab(tabs.at(0)?.id ?? DEFAULT_TAB_ID);
      return;
    }

    if (
      requestedTabId &&
      !hasUserSelectedTabRef.current &&
      tabs.some((tab) => tab.id === requestedTabId) &&
      activeTab !== requestedTabId
    ) {
      setActiveTab(requestedTabId);
    }
  }, [activeTab, requestedTabId, tabs]);

  const isPageLoading =
    !currentAccount ||
    !details ||
    isRelationshipsLoading ||
    isTransfersLoading ||
    acatsTransfers.isLoading ||
    contraBrokers.isLoading ||
    isCryptoLoading ||
    isCryptoMFAStatusLoading;

  return (
    <Page title="Transfer History" className="p-2">
      <div className="px-2">
        {isPageLoading ? (
          <div className="flex items-center justify-center py-24">
            <Spinner />
          </div>
        ) : (
          <>
            {showTabs && (
              <div className="mb-6">
                <Tabs
                  tabs={tabs}
                  activeTab={activeTab}
                  onChange={onTabChange}
                />
              </div>
            )}

            {activeTab === "portfolio" && (
              <PortfolioTransferTable
                transfers={acatsTransfers.data?.transfers || []}
                accountId={currentAccount?.id || ""}
                contraBrokers={contraBrokers.data?.brokers || []}
              />
            )}

            {activeTab === "crypto" && (
              <MFAGuard requireMFAForAllAccounts>
                <CryptoTransferHistoryTable
                  transfers={cryptoTransfers.data || []}
                  assets={cryptoAssets.data || []}
                />
              </MFAGuard>
            )}

            {activeTab === "currency" && (
              <TransferHistoryTable
                account={currentAccount}
                relationships={relationships}
                transfers={transfers}
              />
            )}
          </>
        )}
      </div>
    </Page>
  );
};

export default FundingHistoryPage;
